// Public demographic codes accepted by the broadcast audience endpoint.
// Empty selections mean "all values".
export enum DemographicAgeBracket {
    UNDER_18 = 'UNDER_18',
    AGE_18_24 = 'AGE_18_24',
    AGE_25_34 = 'AGE_25_34',
    AGE_35_49 = 'AGE_35_49',
    AGE_50_64 = 'AGE_50_64',
    AGE_65_PLUS = 'AGE_65_PLUS',
}

/**
 * Values of the endpoint's `audienceScopes` request field and of
 * `audienceScope` on every response row. `P` is not a gender: it is the
 * measured all-persons figure, and it does not equal Male + Female — each
 * scope is measured and rounded independently, and the Under 18 band is only
 * ever reported as `P`.
 */
export enum AudienceScope {
    PERSONS = 'P',
    MALE = 'M',
    FEMALE = 'F',
}

// The Gender filter only offers the two gendered scopes.
export type DemographicGender = AudienceScope.MALE | AudienceScope.FEMALE;

export const DEMOGRAPHIC_AGE_BRACKETS: DemographicAgeBracket[] = [
    DemographicAgeBracket.UNDER_18,
    DemographicAgeBracket.AGE_18_24,
    DemographicAgeBracket.AGE_25_34,
    DemographicAgeBracket.AGE_35_49,
    DemographicAgeBracket.AGE_50_64,
    DemographicAgeBracket.AGE_65_PLUS,
];

export const DEMOGRAPHIC_GENDERS: DemographicGender[] = [
    AudienceScope.MALE,
    AudienceScope.FEMALE,
];

export const AGE_BRACKET_LABEL_KEYS: Record<DemographicAgeBracket, string> = {
    [DemographicAgeBracket.UNDER_18]: 'filters.ageBrackets.under18',
    [DemographicAgeBracket.AGE_18_24]: 'filters.ageBrackets.age18to24',
    [DemographicAgeBracket.AGE_25_34]: 'filters.ageBrackets.age25to34',
    [DemographicAgeBracket.AGE_35_49]: 'filters.ageBrackets.age35to49',
    [DemographicAgeBracket.AGE_50_64]: 'filters.ageBrackets.age50to64',
    [DemographicAgeBracket.AGE_65_PLUS]: 'filters.ageBrackets.age65plus',
};

export const GENDER_LABEL_KEYS: Record<DemographicGender, string> = {
    [AudienceScope.MALE]: 'filters.genders.male',
    [AudienceScope.FEMALE]: 'filters.genders.female',
};

export interface DemographicFilters {
    ageBrackets: DemographicAgeBracket[];
    genders: DemographicGender[];
}

export const EMPTY_DEMOGRAPHIC_FILTERS: DemographicFilters = {
    ageBrackets: [],
    genders: [],
};

/**
 * Maps the UI Gender selection onto the request field the endpoint actually
 * reads, which is `audienceScopes` — *not* `genders`. Unknown request fields
 * are silently dropped, so sending the wrong name is a no-op that still
 * returns 200 with unfiltered data.
 *
 * An empty selection is sent as an empty array, which the endpoint reads as
 * "every scope". When a gender is selected we still ask for `P` so the
 * Demographics table's Total column keeps its measured all-persons value.
 */
export const toAudienceScopes = (
    genders?: DemographicGender[] | null,
): AudienceScope[] =>
    !genders?.length ? [] : [AudienceScope.PERSONS, ...genders];
